/** @jsxImportSource @emotion/react */
import {
  Button,
  createStyles,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsyncFn, useUpdateEffect } from 'react-use';
import { signOut } from 'api';
import { Image, Layout, Markdown, QBLoader } from 'components';
import { useClearSession, useNavigateWithSnapshots } from 'context';
import { closeAccount } from './api';

export const DeleteAccount = () => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);
  const { spacing, shape } = useTheme();
  const clearSession = useClearSession();
  const navigate = useNavigateWithSnapshots();

  const [{ loading, error }, deleteAccount] = useAsyncFn(async () => {
    return await closeAccount();
  });

  useUpdateEffect(() => {
    !error && !loading && setShowSuccess(true);
  }, [error, loading]);

  const handleLogout = async () => {
    void clearSession();
    await signOut();
    navigate('/login', 'none');
  };

  if (loading) return <QBLoader />;

  return (
    <Layout
      primaryButtonProps={{
        text: formatMessage({
          id: showSuccess ? 'logout' : 'continue',
        }),
        onClick: showSuccess ? handleLogout : deleteAccount,
        disabled: !!error,
      }}
    >
      {!showSuccess && (
        <div css={styles.close}>
          <Button
            variant="icon"
            icon="close"
            onClick={() => navigate(-1, 'none')}
          />
        </div>
      )}
      <div
        css={[
          { img: { borderRadius: shape.borderRadius } },
          showSuccess
            ? { marginTop: spacing.xxs }
            : { marginTop: `-${spacing.s}` },
        ]}
      >
        <Image name="dawn" size="100%" />
      </div>
      <div css={styles.main}>
        <div css={styles.bodyText}>
          <div css={styles.title}>
            <Typography variant="heading2">
              {formatMessage({
                id: error
                  ? 'page.accountInfo.deleteAccount.error.heading'
                  : showSuccess
                  ? 'page.accountInfo.deleteAccount.success.heading'
                  : 'page.accountInfo.listItem.deleteAccount',
              })}
            </Typography>
          </div>
          <div>
            {
              <Markdown
                markdown={formatMessage({
                  id: error
                    ? 'page.accountInfo.deleteAccount.error.body'
                    : showSuccess
                    ? 'page.accountInfo.deleteAccount.success.body'
                    : 'page.accountInformation.deleteAccount.text.body',
                })}
              />
            }
          </div>
        </div>
      </div>
    </Layout>
  );
};

const useStyles = createStyles(({ spacing, palette, sizing, breakpoints }) => ({
  main: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [breakpoints.laptopSmall]: {
      justifyContent: 'start',
      height: '100%',
    },
  },
  close: {
    paddingTop: spacing.xxs,
    svg: {
      color: palette.grey.default,
    },
  },
  bodyText: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: spacing.s,
    paddingRight: spacing.s,
    gap: spacing.xxs,
    width: '100%',
    position: 'fixed',
    maxHeight: '50%',
    overflow: 'auto',
    [breakpoints.laptopSmall]: {
      maxHeight: sizing.small,
      position: 'unset',
    },
  },
  title: {
    borderBottom: `1px solid ${palette.grey.x200}`,
    paddingBottom: spacing.xs,
    width: '100%',
  },
}));

export default DeleteAccount;
